import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';
import { Bell, BellOff, Calendar, MapPin, CheckCircle, DollarSign, Eye } from 'lucide-react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Notifications() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showEventDialog, setShowEventDialog] = useState(false);
  const [eventMembershipStatus, setEventMembershipStatus] = useState(null);
  const [joiningEvent, setJoiningEvent] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(response.data || []);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/notifications/${notificationId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update local state
      setNotifications(prev => 
        prev.map(notif => 
          notif.notification_id === notificationId 
            ? { ...notif, is_read: true } 
            : notif
        )
      );
      toast.success('Notification marked as read');
    } catch (error) {
      console.error('Error marking notification as read:', error);
      toast.error('Failed to mark notification as read');
    }
  };

  const handleViewEventDetails = async (eventId, notificationId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/events/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedEvent(response.data);
      
      // Check membership status
      await checkEventMembership(eventId);
      
      setShowEventDialog(true);
      
      // Mark as read
      if (!notifications.find(n => n.notification_id === notificationId)?.is_read) {
        markAsRead(notificationId);
      }
    } catch (error) {
      console.error('Error fetching event details:', error);
      toast.error('Failed to load event details');
    }
  };

  const checkEventMembership = async (eventId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/events/${eventId}/membership-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEventMembershipStatus(response.data.status);
    } catch (error) {
      setEventMembershipStatus(null);
    }
  };

  const handleJoinEvent = async (eventId) => {
    setJoiningEvent(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/events/${eventId}/join`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Successfully joined the event!');
      await checkEventMembership(eventId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to join event');
    } finally {
      setJoiningEvent(false);
    }
  };

  const handleViewEvent = (eventId, notificationId) => {
    if (!notifications.find(n => n.notification_id === notificationId)?.is_read) {
      markAsRead(notificationId);
    }
    navigate(`/customer/events`);
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading notifications...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4 sm:p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 sm:mb-8"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">Notifications</h1>
              <p className="text-sm sm:text-base text-gray-600">
                {unreadCount > 0 ? (
                  <span className="font-semibold text-indigo-600">
                    {unreadCount} unread notification{unreadCount !== 1 ? 's' : ''}
                  </span>
                ) : (
                  'All caught up!'
                )}
              </p>
            </div>
            <div className="bg-indigo-100 p-3 sm:p-4 rounded-full self-start sm:self-auto">
              {unreadCount > 0 ? (
                <Bell className="w-6 h-6 sm:w-8 sm:h-8 text-indigo-600" />
              ) : (
                <BellOff className="w-6 h-6 sm:w-8 sm:h-8 text-gray-400" />
              )}
            </div>
          </div>
        </motion.div>

        {/* Notifications List */}
        {notifications.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12"
          >
            <BellOff className="w-12 h-12 sm:w-16 sm:h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg sm:text-xl font-semibold text-gray-700 mb-2">No notifications yet</h3>
            <p className="text-sm sm:text-base text-gray-500 px-4">
              You'll be notified when businesses you've joined create new events
            </p>
          </motion.div>
        ) : (
          <div className="space-y-3 sm:space-y-4">
            {notifications.map((notification, index) => (
              <motion.div
                key={notification.notification_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Card className={`p-4 sm:p-6 hover:shadow-lg transition-shadow ${
                  !notification.is_read ? 'bg-indigo-50 border-indigo-200' : 'bg-white'
                }`}>
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start gap-2 mb-2">
                        {!notification.is_read && (
                          <span className="w-2 h-2 bg-indigo-600 rounded-full mt-2 flex-shrink-0"></span>
                        )}
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-base sm:text-lg text-gray-900 break-words">
                            {notification.event_name}
                          </h3>
                          {notification.is_read && (
                            <CheckCircle className="w-4 h-4 text-green-500 inline ml-2" />
                          )}
                        </div>
                      </div>
                      
                      <p className="text-sm sm:text-base text-gray-700 mb-3 break-words">{notification.message}</p>
                      
                      <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 sm:gap-4 text-xs sm:text-sm text-gray-600 mb-3">
                        <div className="flex items-center gap-1">
                          <MapPin className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                          <span className="truncate">{notification.business_name}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                          <span className="text-xs sm:text-sm">
                            {new Date(notification.start_date).toLocaleDateString()} - {new Date(notification.end_date).toLocaleDateString()}
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <span>
                          {new Date(notification.created_at).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      </div>
                    </div>

                    <div className="flex sm:flex-col gap-2 justify-end sm:justify-start">
                      <Button
                        onClick={() => handleViewEventDetails(notification.event_id, notification.notification_id)}
                        className="bg-indigo-600 hover:bg-indigo-700 flex-1 sm:flex-initial text-sm"
                        size="sm"
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        View Details
                      </Button>
                      {!notification.is_read && (
                        <Button
                          onClick={() => markAsRead(notification.notification_id)}
                          variant="outline"
                          size="sm"
                          className="flex-1 sm:flex-initial text-sm"
                        >
                          Mark as read
                        </Button>
                      )}
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        )}

        {/* Event Details Dialog */}
        <Dialog open={showEventDialog} onOpenChange={setShowEventDialog}>
          <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto rounded-3xl">
            <DialogHeader>
              <DialogTitle className="font-playfair text-2xl text-primary">Event Details</DialogTitle>
            </DialogHeader>
            {selectedEvent && (
              <div className="space-y-4">
                <div>
                  <h3 className="font-playfair font-bold text-xl text-primary mb-2">{selectedEvent.event_name}</h3>
                  <p className="text-sm text-gray-600 mb-1">By {selectedEvent.business_name}</p>
                  <p className="text-gray-700">{selectedEvent.event_description}</p>
                </div>

                <div className="grid grid-cols-2 gap-4 py-4 border-y">
                  <div>
                    <div className="flex items-center text-gray-600 mb-2">
                      <Calendar className="w-4 h-4 mr-2" />
                      <span className="text-sm font-semibold">Start Date</span>
                    </div>
                    <p className="text-sm">{new Date(selectedEvent.start_date).toLocaleDateString()}</p>
                  </div>
                  <div>
                    <div className="flex items-center text-gray-600 mb-2">
                      <Calendar className="w-4 h-4 mr-2" />
                      <span className="text-sm font-semibold">End Date</span>
                    </div>
                    <p className="text-sm">{new Date(selectedEvent.end_date).toLocaleDateString()}</p>
                  </div>
                  {/* Only show coupon value if not hidden */}
                  {!selectedEvent.coupon_value_hidden && (
                    <div>
                      <div className="flex items-center text-gray-600 mb-2">
                        <span className="text-sm font-semibold">Incentive Points</span>
                      </div>
                      <p className="text-sm font-bold text-green-600">{selectedEvent.coupon_value}</p>
                    </div>
                  )}
                  <div>
                    <div className="flex items-center text-gray-600 mb-2">
                      <MapPin className="w-4 h-4 mr-2" />
                      <span className="text-sm font-semibold">Status</span>
                    </div>
                    <p className="text-sm capitalize">{selectedEvent.status}</p>
                  </div>
                </div>

                {selectedEvent.prizes && selectedEvent.prizes.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-gray-700 mb-2">Prizes:</h4>
                    <ul className="space-y-2">
                      {selectedEvent.prizes.map((prize, idx) => (
                        <li key={idx} className="flex items-start gap-3 text-sm">
                          <span className="text-amber-600 font-bold mt-0.5">{idx + 1}.</span>
                          <div className="flex-1">
                            <div className="flex flex-wrap items-baseline gap-2">
                              <span className="font-semibold text-gray-800">{prize.prize_name || `Prize ${idx + 1}`}</span>
                              <span className="text-amber-600 font-bold text-base">${prize.prize_amount}</span>
                            </div>
                            {prize.prize_description && (
                              <p className="text-gray-600 text-xs mt-1">{prize.prize_description}</p>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="flex flex-col gap-2 pt-4">
                  {eventMembershipStatus === 'approved' ? (
                    <div className="flex items-center justify-center gap-2 text-green-600 font-semibold py-2 bg-green-50 rounded-lg">
                      <CheckCircle className="w-5 h-5" />
                      Already Joined
                    </div>
                  ) : eventMembershipStatus === 'pending' ? (
                    <div className="flex items-center justify-center gap-2 text-yellow-600 font-semibold py-2 bg-yellow-50 rounded-lg">
                      <Bell className="w-5 h-5" />
                      Join Request Pending
                    </div>
                  ) : (
                    <Button
                      onClick={() => handleJoinEvent(selectedEvent.event_id)}
                      disabled={joiningEvent}
                      className="w-full bg-green-600 hover:bg-green-700"
                    >
                      {joiningEvent ? 'Joining...' : 'Join Event'}
                    </Button>
                  )}
                  
                  <div className="flex gap-2">
                    <Button
                      onClick={() => {
                        setShowEventDialog(false);
                        navigate('/customer/events');
                      }}
                      variant="outline"
                      className="flex-1"
                    >
                      Go to Events Page
                    </Button>
                    <Button
                      onClick={() => setShowEventDialog(false)}
                      variant="outline"
                      className="flex-1"
                    >
                      Close
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
